import { UserProfile, UserStats, GameSession, UnlockedAchievement, MeasurementStat } from '../types/profile';
import { ACHIEVEMENTS, getAchievementById } from '../data/achievements';

// Local storage keys
const PROFILE_KEY = 'banana_game_profile';
const GUEST_AVATARS = ['🐵', '🦍', '🍌', '🎮', '🎯', '🏆', '⭐', '🚀'];

// Profile service - designed for easy Supabase migration
export class ProfileService {
  
  // Initialize or get existing profile (seamless guest experience)
  static async getOrCreateProfile(): Promise<UserProfile> {
    try {
      const stored = localStorage.getItem(PROFILE_KEY);
      if (stored) {
        const profile = JSON.parse(stored) as UserProfile;
        // Update last active
        profile.lastActiveAt = new Date().toISOString();
        this.saveProfile(profile);
        return profile;
      }
    } catch (error) {
      console.warn('Error loading profile, creating new one:', error);
    }

    // Create new guest profile
    return this.createGuestProfile();
  }

  // Create a new guest profile with random avatar
  private static createGuestProfile(): UserProfile {
    const now = new Date().toISOString();
    const guestNumber = Math.floor(Math.random() * 9999) + 1;
    const avatar = GUEST_AVATARS[Math.floor(Math.random() * GUEST_AVATARS.length)];
    
    const profile: UserProfile = {
      id: `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, // UUID-like for Supabase migration
      username: `Guest${guestNumber}`,
      avatar,
      createdAt: now,
      lastActiveAt: now,
      stats: this.createEmptyStats(),
      achievements: [],
      preferences: {
        favoriteGameMode: 'classic',
        preferredDifficulty: 'medium',
        soundEnabled: true,
        animationsEnabled: true
      }
    };

    this.saveProfile(profile);
    return profile;
  }

  // Create empty stats structure
  private static createEmptyStats(): UserStats {
    const emptyMeasurementStat: MeasurementStat = {
      gamesPlayed: 0,
      questionsAnswered: 0,
      correctAnswers: 0,
      totalScore: 0,
      averageAccuracy: 0,
      bestStreak: 0
    };

    return {
      totalGames: 0,
      totalScore: 0,
      bestScore: 0,
      longestStreak: 0,
      currentStreak: 0,
      gamesWon: 0,
      totalTimePlayed: 0,
      averageAccuracy: 0,
      measurementStats: {
        length: { ...emptyMeasurementStat },
        weight: { ...emptyMeasurementStat },
        volume: { ...emptyMeasurementStat }
      },
      gameModeStats: {}
    };
  }

  // Save profile to localStorage (will become Supabase upsert)
  static saveProfile(profile: UserProfile): void {
    try {
      localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
    } catch (error) {
      console.error('Error saving profile:', error);
    }
  }

  // Update profile after game completion
  static async updateProfileAfterGame(gameSession: GameSession): Promise<UnlockedAchievement[]> {
    const profile = await this.getOrCreateProfile();
    const newAchievements: UnlockedAchievement[] = [];

    // Update basic stats
    profile.stats.totalGames += 1;
    profile.stats.totalScore += gameSession.score;
    profile.stats.bestScore = Math.max(profile.stats.bestScore, gameSession.score);
    profile.stats.totalTimePlayed += gameSession.timePlayed;
    
    // Update streak
    if (gameSession.correctAnswers === gameSession.questionsAnswered) {
      profile.stats.currentStreak += 1;
      profile.stats.longestStreak = Math.max(profile.stats.longestStreak, profile.stats.currentStreak);
    } else {
      profile.stats.currentStreak = 0;
    }

    // Update games won (>70% accuracy)
    const accuracy = gameSession.questionsAnswered > 0 ? 
      (gameSession.correctAnswers / gameSession.questionsAnswered) * 100 : 0;
    if (accuracy >= 70) {
      profile.stats.gamesWon += 1;
    }

    // Calculate new average accuracy
    const totalQuestions = Object.values(profile.stats.measurementStats)
      .reduce((sum, stat) => sum + stat.questionsAnswered, 0) + gameSession.questionsAnswered;
    const totalCorrect = Object.values(profile.stats.measurementStats)
      .reduce((sum, stat) => sum + stat.correctAnswers, 0) + gameSession.correctAnswers;
    profile.stats.averageAccuracy = totalQuestions > 0 ? (totalCorrect / totalQuestions) * 100 : 0;

    // Update measurement-specific stats
    if (gameSession.measurementType) {
      const measurementStat = profile.stats.measurementStats[gameSession.measurementType];
      measurementStat.gamesPlayed += 1;
      measurementStat.questionsAnswered += gameSession.questionsAnswered;
      measurementStat.correctAnswers += gameSession.correctAnswers;
      measurementStat.totalScore += gameSession.score;
      measurementStat.averageAccuracy = measurementStat.questionsAnswered > 0 ?
        (measurementStat.correctAnswers / measurementStat.questionsAnswered) * 100 : 0;
      measurementStat.bestStreak = Math.max(measurementStat.bestStreak, gameSession.streak);
    }

    // Update game mode stats
    if (!profile.stats.gameModeStats[gameSession.gameMode]) {
      profile.stats.gameModeStats[gameSession.gameMode] = {
        gamesPlayed: 0,
        bestScore: 0,
        averageScore: 0,
        totalTimePlayed: 0
      };
    }
    const gameModeStat = profile.stats.gameModeStats[gameSession.gameMode];
    gameModeStat.gamesPlayed += 1;
    gameModeStat.bestScore = Math.max(gameModeStat.bestScore, gameSession.score);
    gameModeStat.totalTimePlayed += gameSession.timePlayed;
    gameModeStat.averageScore = gameModeStat.gamesPlayed > 0 ?
      Object.values(profile.stats.gameModeStats)
        .filter(stat => stat.gamesPlayed > 0)
        .reduce((sum, stat) => sum + (stat.bestScore / stat.gamesPlayed), 0) / 
      Object.values(profile.stats.gameModeStats).filter(stat => stat.gamesPlayed > 0).length : 0;

    // Check for new achievements
    const unlockedAchievements = this.checkAchievements(profile, gameSession);
    newAchievements.push(...unlockedAchievements);

    // Update last active time
    profile.lastActiveAt = new Date().toISOString();

    // Save updated profile
    this.saveProfile(profile);

    return newAchievements;
  }

  // Check for newly unlocked achievements
  private static checkAchievements(profile: UserProfile, gameSession: GameSession): UnlockedAchievement[] {
    const newAchievements: UnlockedAchievement[] = [];
    const unlockedIds = new Set(profile.achievements.map(a => a.id));

    for (const achievement of ACHIEVEMENTS) {
      if (unlockedIds.has(achievement.id)) continue;

      let unlocked = false;
      const req = achievement.requirement;

      switch (req.type) {
        case 'games_played':
          unlocked = profile.stats.totalGames >= req.value;
          break;
        
        case 'total_score':
          // Special case for single game high score
          if (achievement.id === 'high_scorer') {
            unlocked = gameSession.score >= req.value;
          } else {
            unlocked = profile.stats.totalScore >= req.value;
          }
          break;
        
        case 'streak':
          unlocked = Math.max(profile.stats.longestStreak, gameSession.streak) >= req.value;
          break;
        
        case 'accuracy':
          unlocked = profile.stats.averageAccuracy >= req.value;
          break;
        
        case 'measurement_games':
          if (req.measurementType) {
            const stat = profile.stats.measurementStats[req.measurementType];
            unlocked = stat.gamesPlayed >= req.value;
          } else if (achievement.id === 'measurement_master') {
            // Special case: all measurement types with 15+ games each
            unlocked = Object.values(profile.stats.measurementStats)
              .every(stat => stat.gamesPlayed >= 15);
          }
          break;
        
        case 'game_mode_games':
          if (req.difficulty) {
            // Count games with specific difficulty
            const count = Object.entries(profile.stats.gameModeStats)
              .reduce((sum, [_mode, stat]) => {
                // This is simplified - in real implementation, we'd track difficulty per game
                return sum + stat.gamesPlayed;
              }, 0);
            unlocked = count >= req.value;
          } else if (achievement.id === 'mode_explorer') {
            // Special case: played all 6 different game modes
            unlocked = Object.keys(profile.stats.gameModeStats).length >= 6;
          }
          break;
        
        case 'perfect_questions':
          // This would need to be tracked separately in a real implementation
          // For now, estimate based on high accuracy
          const estimatedPerfect = Math.floor(profile.stats.averageAccuracy / 10);
          unlocked = estimatedPerfect >= req.value;
          break;
      }

      if (unlocked) {
        const unlockedAchievement: UnlockedAchievement = {
          id: achievement.id,
          unlockedAt: new Date().toISOString()
        };
        newAchievements.push(unlockedAchievement);
        profile.achievements.push(unlockedAchievement);
      }
    }

    return newAchievements;
  }

  // Get user's unlocked achievements with details
  static async getUnlockedAchievements(): Promise<Array<UnlockedAchievement & { achievement: any }>> {
    const profile = await this.getOrCreateProfile();
    return profile.achievements.map(unlocked => ({
      ...unlocked,
      achievement: getAchievementById(unlocked.id)
    })).filter(item => item.achievement);
  }

  // Update user preferences
  static async updatePreferences(updates: Partial<UserProfile['preferences']>): Promise<void> {
    const profile = await this.getOrCreateProfile();
    profile.preferences = { ...profile.preferences, ...updates };
    profile.lastActiveAt = new Date().toISOString();
    this.saveProfile(profile);
  }

  // Update username and avatar
  static async updateProfile(updates: { username?: string; avatar?: string }): Promise<void> {
    const profile = await this.getOrCreateProfile();
    if (updates.username) profile.username = updates.username;
    if (updates.avatar) profile.avatar = updates.avatar;
    profile.lastActiveAt = new Date().toISOString();
    this.saveProfile(profile);
  }

  // Get leaderboard data (local only for now)
  static async getLocalStats(): Promise<UserProfile['stats']> {
    const profile = await this.getOrCreateProfile();
    return profile.stats;
  }

  // Reset profile (for testing or user request)
  static resetProfile(): void {
    localStorage.removeItem(PROFILE_KEY);
  }

  // Export profile data (for future Supabase migration)
  static async exportProfile(): Promise<UserProfile> {
    return await this.getOrCreateProfile();
  }

  /*
   * FUTURE SUPABASE MIGRATION NOTES:
   * 
   * 1. Replace localStorage with Supabase client calls
   * 2. Add authentication flow (sign up, sign in, guest -> permanent account)
   * 3. Create tables:
   *    - profiles (maps to UserProfile)
   *    - game_sessions (maps to GameSession)
   *    - achievements (static data)
   *    - user_achievements (maps to UnlockedAchievement)
   * 
   * 4. Add real-time subscriptions for live updates
   * 5. Add social features (friends, leaderboards)
   * 6. Add cloud backup/restore functionality
   * 7. Add proper error handling and offline support
   * 
   * Migration strategy:
   * - Keep same interface, swap implementation
   * - Add migration function to upload localStorage data
   * - Implement progressive enhancement (offline-first)
   */
} 